export type SharedCheckoutCartItem = {
  readonly skuId: string;
  readonly quantity: number;
};

export type SharedCheckoutSelection = {
  readonly servicePath: string;
  readonly serviceName: string;
  readonly tierSkuId: string;
  readonly tierName: string;
  readonly cartItems: readonly SharedCheckoutCartItem[];
  readonly laneIds?: readonly string[];
  readonly laneLabels?: readonly string[];
  readonly addOnLabels?: readonly string[];
  readonly createdAt: number;
};

type SharedCheckoutSelectionV1 = SharedCheckoutSelection & {
  readonly v: 1;
};

function sanitizeCartItems(items: readonly SharedCheckoutCartItem[]): SharedCheckoutCartItem[] {
  return items
    .map((item) => ({
      skuId: String(item.skuId ?? '').trim(),
      quantity: Number(item.quantity ?? 0),
    }))
    .filter((item) => item.skuId.length > 0 && Number.isInteger(item.quantity) && item.quantity > 0);
}

function sanitizeStringArray(values: readonly string[] | undefined): string[] | undefined {
  if (!Array.isArray(values) || !values.length) return undefined;
  const next = values.map((value) => String(value ?? '').trim()).filter(Boolean);
  return next.length ? next : undefined;
}

function toBase64Url(raw: string): string {
  const bytes = new TextEncoder().encode(raw);
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function fromBase64Url(encoded: string): string {
  const normalized = encoded.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '==='.slice((normalized.length + 3) % 4);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

export function createSharedCheckoutToken(input: SharedCheckoutSelection): string {
  const payload: SharedCheckoutSelectionV1 = {
    v: 1,
    servicePath: String(input.servicePath ?? '').trim(),
    serviceName: String(input.serviceName ?? '').trim(),
    tierSkuId: String(input.tierSkuId ?? '').trim(),
    tierName: String(input.tierName ?? '').trim(),
    cartItems: sanitizeCartItems(input.cartItems),
    laneIds: sanitizeStringArray(input.laneIds),
    laneLabels: sanitizeStringArray(input.laneLabels),
    addOnLabels: sanitizeStringArray(input.addOnLabels),
    createdAt: Number.isFinite(input.createdAt) ? input.createdAt : Date.now(),
  };

  if (!payload.servicePath || !payload.tierSkuId || !payload.cartItems.length) {
    throw new Error('Missing required share payload fields.');
  }

  return toBase64Url(JSON.stringify(payload));
}

export function parseSharedCheckoutToken(token: string): SharedCheckoutSelection | null {
  try {
    const decoded = fromBase64Url(String(token ?? '').trim());
    const parsed = JSON.parse(decoded) as Partial<SharedCheckoutSelectionV1>;
    if (!parsed || parsed.v !== 1) return null;

    const cartItems = sanitizeCartItems(parsed.cartItems ?? []);
    if (!cartItems.length) return null;

    const servicePath = String(parsed.servicePath ?? '').trim();
    const tierSkuId = String(parsed.tierSkuId ?? '').trim();
    if (!servicePath || !tierSkuId) return null;

    return {
      servicePath,
      serviceName: String(parsed.serviceName ?? '').trim() || 'Shared configuration',
      tierSkuId,
      tierName: String(parsed.tierName ?? '').trim() || tierSkuId,
      cartItems,
      laneIds: sanitizeStringArray(parsed.laneIds),
      laneLabels: sanitizeStringArray(parsed.laneLabels),
      addOnLabels: sanitizeStringArray(parsed.addOnLabels),
      createdAt: Number.isFinite(Number(parsed.createdAt))
        ? Number(parsed.createdAt)
        : Date.now(),
    };
  } catch {
    return null;
  }
}

export function buildSharedCheckoutUrl(payload: SharedCheckoutSelection): string {
  const token = createSharedCheckoutToken(payload);
  const url = new URL('/checkout', window.location.origin);
  url.searchParams.set('share', token);
  return url.toString();
}
